import type { ReactElement } from "react";

import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { tenantUserUrls } from "./urls.js";

/** 招待のフォーム。欄の名前は API が受ける ViewModel と同じ（<c>InviteViewModel</c>）。 */
function InviteForm({ tenantId }: { readonly tenantId: number }): ReactElement {
    const form = useApiForm(tenantUserUrls.api.invite(tenantId));
    const emailError = form.fieldError("Email");

    return (
        <form method="post" onSubmit={form.onSubmit}>
            <FormSummary messages={form.summary} />

            <div className="mb-4">
                <label className="form-label" htmlFor="Email">メールアドレス</label>
                <input
                    className={emailError === undefined ? "form-input" : "form-input is-invalid"}
                    id="Email" name="Email" type="email" placeholder="mail@example.com" required
                    aria-invalid={emailError === undefined ? undefined : true}
                />
                {emailError === undefined
                    ? null
                    : <span className="form-error field-validation-error" data-valmsg-for="Email">{emailError}</span>}
            </div>
            <label className="mb-5 flex items-center gap-2 text-sm">
                <input className="form-check" id="Admin" name="Admin" type="checkbox" value="true" />
                <input type="hidden" name="Admin" value="false" />
                <span>管理者として招待する</span>
            </label>
            <button type="submit" className="btn-primary w-full" disabled={form.busy}>招待メールを送信</button>
        </form>
    );
}

/**
 * 招待の欄。<b>契約人数に達していれば欄の代わりに断りを出す</b>
 * （上限の判定は絞り込む前の総数で、サーバが決める）。
 * <b>会員がプラン変更できない契約（無償利用・月額以外）は、プラン変更を勧めず運営へのお問い合わせを案内する</b>（<c>canChangePlan</c>。サーバが決める）。
 */
export function InvitePanel({ tenantId, full, canChangePlan }: {
    readonly tenantId: number;
    readonly full: boolean;
    readonly canChangePlan: boolean;
}): ReactElement {
    return (
        <section className="card min-w-0 p-6 lg:col-span-2">
            <h2 className="section-title">ユーザを招待する</h2>

            {full
                ? (
                    <div className="alert-warning">
                        登録できる人数の上限に達しているため、これ以上招待できません。<br />
                        {canChangePlan
                            ? "招待するには、不要な利用者を削除するか、ご契約者がプラン変更で利用人数を増やしてください。"
                            : (
                                <>
                                    招待するには、不要な利用者を削除してください。利用人数を増やすプランの変更は運営にお問い合わせください。
                                    <span className="mt-3 block" data-plan-change-contact="">
                                        <a className="btn-secondary btn-sm" href="/Home/Contact">お問い合わせ</a>
                                    </span>
                                </>
                            )}
                    </div>
                )
                : <InviteForm tenantId={tenantId} />}
        </section>
    );
}
